import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { ComponentProps } from 'react';

import { DivisionChange } from '@sorare/core/src/__generated__/globalTypes';
import { Scrollable } from '@sorare/core/src/atoms/navigation/Scrollable';
import { useResultsStoryAssets } from '@sorare/core/src/components/animations/AnimatedStory/useResultsStoryAssets';
import { useIsTabletAndAbove } from '@sorare/core/src/hooks/device/useIsTabletAndAbove';

import { LastFixtureStoryStep } from 'components/so5/LastFixtureStory/LastFixtureStoryStep';
import type { StoryState } from 'components/so5/LastFixtureStory/types';

import { DELAY_BEFORE_ANIMATION_MS } from '../CardLayout';
import { Title } from '../LastFixtureStoryStep/Title';
import { LeaderboardDivisionChangeRecap } from '../LeaderboardDivisionChangeRecap';
import type { Relegation_so5Fixture } from './__generated__/index.graphql';

type Props = {
  from?: StoryState;
  so5Fixture?: Relegation_so5Fixture;
  onNext?: () => void;
  onClose: ComponentProps<typeof LastFixtureStoryStep>['onClose'];
};
export const Relegation = ({ from, so5Fixture, onNext, onClose }: Props) => {
  const isTabletAndAbove = useIsTabletAndAbove();

  const { intro, tunnel } = useResultsStoryAssets();

  const displayedRankings =
    so5Fixture?.mySo5Rankings.filter(
      ranking => ranking.eligibleDivisionChange === DivisionChange.RELEGATED
    ) || [];
  const itemToDisplayOnDesktop = Math.min(3, displayedRankings.length);
  const itemToDisplay = isTabletAndAbove ? itemToDisplayOnDesktop : 1;

  return (
    <LastFixtureStoryStep
      title={<Title type="relegation" so5Fixture={so5Fixture} />}
      onClose={onClose}
      video={{
        asset: from === 'intro' ? intro : tunnel,
        color: '#C42500',
        colorDelay: from === 'intro' ? 800 : 0,
      }}
      onNext={onNext}
    >
      <div
        style={{
          width: `calc(${itemToDisplay} * 250px + var(--double-unit) * ${itemToDisplay - 1})`,
        }}
      >
        <Scrollable
          withMask
          itemToDisplay={itemToDisplay}
          visibleVerticalOverflowSize={100}
        >
          {displayedRankings.map(ranking => (
            <LeaderboardDivisionChangeRecap
              key={ranking.id}
              delay={DELAY_BEFORE_ANIMATION_MS}
              so5Ranking={ranking}
            />
          ))}
        </Scrollable>
      </div>
    </LastFixtureStoryStep>
  );
};

Relegation.fragments = {
  so5Fixture: gql`
    fragment Relegation_so5Fixture on So5Fixture {
      slug
      mySo5Rankings {
        id
        eligibleDivisionChange
        ...LeaderboardDivisionChangeRecap_so5Ranking
      }
      ...Title_so5Fixture
    }
    ${LeaderboardDivisionChangeRecap.fragments.so5Ranking}
    ${Title.fragments.so5Fixture}
  ` as TypedDocumentNode<Relegation_so5Fixture>,
};
